/**
 * Issue #316 — 课程音频上传 API 客户端 + 纯展示助手。
 *
 * 红线：真实 multipart 上传到后端 POST /admin/courses/:id/audio（字段名 audio），绝不前端假
 * 成功、绝不把二进制编码成字符串塞进 JSON。错误（413/415/403/非 JSON HTML）由 ApiClient 统一
 * 抛出携带后端中文 message 的 ApiError，弹窗直接渲染 err.message。
 */
import { apiClient } from './api';
import { formatDateTime, formatDuration, formatFileSize } from './format';
import type { CourseAdminView, CourseAudioView } from './types';

/** multipart 字段名（与后端 FileInterceptor('audio') 对齐）。 */
export const COURSE_AUDIO_FIELD = 'audio';

/** 上传 / 替换课程音频。成功返回更新后的课程视图（含 audio）。 */
export function uploadCourseAudioApi(courseId: string, file: File): Promise<CourseAdminView> {
  const form = new FormData();
  form.append(COURSE_AUDIO_FIELD, file, file.name);
  return apiClient.upload<CourseAdminView>(
    `/admin/courses/${encodeURIComponent(courseId)}/audio`,
    form,
  );
}

export interface CourseAudioStateView {
  uploaded: boolean;
  fileName: string;
  size: string;
  duration: string;
  updatedAt: string;
}

/**
 * 把 CourseAudioView 归一化为可直接渲染的中文展示串（未上传 / 已上传两态）。
 * 纯函数，便于单测，不依赖 React / 网络。
 */
export function describeCourseAudio(audio: CourseAudioView | null | undefined): CourseAudioStateView {
  if (!audio || !audio.url) {
    return { uploaded: false, fileName: '尚未上传', size: '--', duration: '--', updatedAt: '--' };
  }
  return {
    uploaded: true,
    fileName: audio.fileName?.trim() ? audio.fileName : '课程音频.mp3',
    size: formatFileSize(audio.sizeBytes),
    duration: audio.durationSec != null ? formatDuration(audio.durationSec) : '--',
    updatedAt: audio.updatedAt ? formatDateTime(audio.updatedAt) : '--',
  };
}
